export interface ServiceContext {
  serviceId: string
  timestamp: string
}

export interface EnhancedError extends Error {
  serviceContext?: ServiceContext
  config?: {
    url?: string
  }
  response?: {
    status?: number
    data?: {
      errorCode?: string
      errorMessage?: string
      error_code?: string // Add support for snake_case
      error_message?: string // Add support for snake_case
    }
  }
}

/**
 * Extracts service information from an enhanced error
 */
export const getServiceFromError = (error: unknown): string | undefined => {
  const enhancedError = error as EnhancedError
  return enhancedError?.serviceContext?.serviceId
}

/**
 * Extracts error code from various error formats
 */
export const getErrorCodeFromError = (error: unknown): string | undefined => {
  const enhancedError = error as EnhancedError

  // Try API response error code first
  if (enhancedError?.response?.data?.errorCode) {
    return enhancedError.response.data.errorCode
  }

  // Try snake_case error code
  if (enhancedError?.response?.data?.error_code) {
    return enhancedError.response.data.error_code
  }

  // Try axios error status
  if (enhancedError?.response?.status) {
    return `HTTP_${enhancedError.response.status}`
  }

  // Fallback to generic error
  return 'UNKNOWN_ERROR'
}

/**
 * Extracts error description from various error formats
 * Priority: API response message → error message → error message with URL → generic fallback
 */
export const getErrorDescriptionFromError = (error: unknown): string => {
  const enhancedError = error as EnhancedError

  // Priority 1: Try API response error message first
  if (enhancedError?.response?.data?.errorMessage) {
    return enhancedError.response.data.errorMessage
  }

  if (enhancedError?.response?.data?.error_message) {
    return enhancedError.response.data.error_message
  }

  // Priority 2: Try error message
  if (enhancedError?.message) {
    return enhancedError.message
  }

  // Priority 3: Include request URL when no response data is available
  if (enhancedError?.config?.url) {
    return `Request failed for URL: ${enhancedError.config.url}`
  }

  // Priority 4: Generic fallback
  return 'An error occurred'
}

/**
 * Creates formatted error info for exitFlow
 */
export const createErrorInfo = (error: unknown) => {
  const serviceId = getServiceFromError(error)
  const errorCode = getErrorCodeFromError(error)
  const errorDescription = getErrorDescriptionFromError(error)

  if (!serviceId) {
    return {
      errorCode,
      errorDescription
    }
  }

  return {
    errorCode: `${serviceId}.${errorCode}`,
    errorDescription
  }
}
